import {AbstractHttpClient} from 'core/abstract/AbstractHttpClient';
import {exception, reqInterceptor, resIntercept} from 'core/decorators';
import {keyBy} from 'core/helpers';
import {AbortRequestInterceptor} from 'core/interceptors/AbortRequestInterceptor';
import {MixHeaderRequestInterceptor} from 'core/interceptors/MixHeaderRequestInterceptor';
import {NodeConsoleResponseLoggerInterceptor} from 'core/interceptors/NodeConsoleResponseLoggerInterceptor';
import {PlatformInfoRequestInterceptor} from 'core/interceptors/PlatformInfoRequestInterceptor';
import {ClientIpRequestInterceptor} from 'core/interceptors/ClientIpRequestInterceptor';
import {RequestIdRequestInterceptor} from 'core/interceptors/RequestIdRequestInterceptor';
import type {IHttpClientResponse, TJson, TMappedServerError, TRequestParameters, TServerError} from 'core/types';
import {API_VERSION, NEXT_PUBLIC_API_HOST} from 'core/helpers/env';

const reqInterceptorsList = [
    new PlatformInfoRequestInterceptor(),
    new MixHeaderRequestInterceptor(),
    new ClientIpRequestInterceptor(),
    new RequestIdRequestInterceptor(),
    AbortRequestInterceptor.getInstance(),
];

const resInterceptorsList = [new NodeConsoleResponseLoggerInterceptor()];

export class HttpClient extends AbstractHttpClient<typeof fetch, RequestInit, Response> {
    public basePath: string = `${NEXT_PUBLIC_API_HOST}/${API_VERSION}`;

    constructor() {
        super(fetch);
    }

    private async customizeRes<Rs>(response: Response): Promise<IHttpClientResponse<Rs>> {
        const result = {
            headers: response.headers,
            raw: response,
            status: response.status,
        };

        try {
            return {
                data: <Rs>await response.json(),
                ...result,
            };
        } catch (e) {
            return result as IHttpClientResponse<Rs>;
        }
    }

    @reqInterceptor(reqInterceptorsList, {method: 'GET'})
    @exception()
    @resIntercept(resInterceptorsList)
    public async get<Rs extends TJson = TJson>({
        url,
        options,
    }: TRequestParameters<RequestInit>): Promise<IHttpClientResponse<Rs>> {
        const currentUrl = `${this.basePath}${url}`;

        const response = await fetch(currentUrl, {
            method: 'GET',
            ...options,
        });

        return this.customizeRes<Rs>(response);
    }

    @reqInterceptor(reqInterceptorsList, {method: 'POST'})
    @resIntercept(resInterceptorsList)
    public async post<Body extends TJson = TJson, Rs extends TJson = TJson>({
        url,
        options,
        ...rest
    }: TRequestParameters<RequestInit, Body>): Promise<IHttpClientResponse<Rs>> {
        const currentUrl = `${this.basePath}${url}`;
        const payload = (rest as {body?: Body}).body;

        const response = await fetch(currentUrl, {
            ...options,
            body: JSON.stringify(payload),
            method: 'POST',
        });

        return this.customizeRes<Rs>(response);
    }

    @reqInterceptor(reqInterceptorsList, {method: 'PUT'})
    @resIntercept(resInterceptorsList)
    public async put<Body extends TJson = TJson, Rs extends TJson = TJson>({
        url,
        options,
        ...rest
    }: TRequestParameters<RequestInit, Body>): Promise<IHttpClientResponse<Rs, Headers, Response>> {
        const currentUrl = `${this.basePath}${url}`;
        const payload = (rest as {body?: Body}).body;
        const response = await fetch(currentUrl, {
            ...options,
            body: JSON.stringify(payload),
            method: 'PUT',
        });

        return this.customizeRes<Rs>(response);
    }

    @reqInterceptor(reqInterceptorsList, {method: 'PATCH'})
    @resIntercept(resInterceptorsList)
    public async patch<Body extends TJson = TJson, Rs extends TJson = TJson>({
        url,
        options,
        ...rest
    }: TRequestParameters<RequestInit, Body>): Promise<IHttpClientResponse<Rs, Headers, Response>> {
        const currentUrl = `${this.basePath}${url}`;

        const payload = (rest as {body?: Body}).body;
        const response = await fetch(currentUrl, {
            ...options,
            body: JSON.stringify(payload),
            method: 'PATCH',
        });

        return this.customizeRes<Rs>(response);
    }

    public async delete<Body extends TJson = TJson, Rs extends TJson = TJson>({
        url,
        options,
        ...rest
    }: TRequestParameters<RequestInit, Body>): Promise<IHttpClientResponse<Rs, Headers, Response>> {
        const currentUrl = `${this.basePath}${url}`;

        const payload = (rest as {body?: Body}).body;
        const response = await fetch(currentUrl, {
            ...options,
            body: JSON.stringify(payload),
            method: 'DELETE',
        });

        return this.customizeRes<Rs>(response);
    }

    public async head<Rs extends TJson = TJson>({
        url,
        options,
    }: TRequestParameters<RequestInit, undefined>): Promise<IHttpClientResponse<Rs, Headers, Response>> {
        const currentUrl = `${this.basePath}${url}`;

        const response = await fetch(currentUrl, {
            ...options,
            method: 'HEAD',
        });

        return this.customizeRes<Rs>(response);
    }

    public static normalizeResponseErrors = <T extends Record<string, any> = Record<string, any>>(
        errorsList: TServerError<T>[],
    ): TMappedServerError<T> => {
        return keyBy<TServerError<T>, true>(errorsList, 'field', ({message}) => message) as TMappedServerError<T>;
    };
}
